import { useState } from 'react';
import type { BannedWord, WordLevel, WordAction } from '@/mocks/adminModeracion';
import { bannedWords, wordLevelLabels, wordLevelColors, wordActionLabels, wordActionIcons } from '@/mocks/adminModeracion';

interface Props {
  words: BannedWord[];
  onAdd: (word: BannedWord) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function WordFilterPanel({ words, onAdd, onToggleActive, onDelete }: Props) {
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<WordLevel | 'todos'>('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [newLevel, setNewLevel] = useState<WordLevel>('medio');
  const [newAction, setNewAction] = useState<WordAction>('reemplazar');

  const filteredWords = words.filter((w) => {
    if (levelFilter !== 'todos' && w.level !== levelFilter) return false;
    if (search && !w.word.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleAdd = () => {
    if (!newWord.trim()) return;
    const word: BannedWord = {
      id: `bw-${Date.now()}`,
      word: newWord.trim(),
      level: newLevel,
      action: newAction,
      addedAt: new Date().toISOString(),
      addedBy: 'Carlos Mendoza',
      active: true,
    };
    onAdd(word);
    setNewWord('');
    setNewLevel('medio');
    setNewAction('reemplazar');
    setFormOpen(false);
  };

  const levels: (WordLevel | 'todos')[] = ['todos', 'leve', 'medio', 'grave'];

  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total palabras', value: words.length, icon: 'ri-file-list-3-line', color: 'text-secondary-500' },
          { label: 'Graves', value: words.filter((w) => w.level === 'grave').length, icon: 'ri-error-warning-line', color: 'text-red-500' },
          { label: 'Medias', value: words.filter((w) => w.level === 'medio').length, icon: 'ri-alert-line', color: 'text-orange-500' },
          { label: 'Leves', value: words.filter((w) => w.level === 'leve').length, icon: 'ri-information-line', color: 'text-amber-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center`}>
                <i className={`${s.icon} ${s.color} text-sm`}></i>
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar palabra..."
            className="w-full pl-8 pr-8 py-2 rounded-xl bg-background-50 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors">
              <i className="ri-close-line text-background-50 text-[8px]"></i>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          {levels.map((l) => (
            <button
              key={l}
              onClick={() => setLevelFilter(l)}
              className={`px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all ${
                levelFilter === l ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {l === 'todos' ? 'Todos' : wordLevelLabels[l]}
            </button>
          ))}
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-sm"></i>
          Agregar palabra
        </button>
      </div>

      {/* Word list */}
      <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Palabra</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Nivel</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Acción</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden md:table-cell">Agregado por</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden md:table-cell">Fecha</th>
                <th className="text-center px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Activo</th>
                <th className="text-center px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filteredWords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
                        <i className="ri-file-search-line text-foreground-300 text-lg"></i>
                      </div>
                      <p className="text-xs text-foreground-400 font-medium">Sin resultados</p>
                      <p className="text-[10px] text-foreground-400">No se encontraron palabras con esos filtros</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredWords.map((w) => (
                  <tr key={w.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-foreground-900 font-mono bg-background-200 px-2 py-0.5 rounded-md">{w.word}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${wordLevelColors[w.level]}`}>
                        {wordLevelLabels[w.level]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <i className={`${wordActionIcons[w.action]} text-foreground-500 text-xs`}></i>
                        <span className="text-[10px] text-foreground-600">{wordActionLabels[w.action]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-[10px] text-foreground-500">{w.addedBy}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-[10px] text-foreground-500">
                        {new Date(w.addedAt).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => onToggleActive(w.id)}
                        className={`w-8 h-4.5 rounded-full relative cursor-pointer transition-colors ${w.active ? 'bg-emerald-500' : 'bg-foreground-300'}`}
                      >
                        <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-background-50 shadow-sm transition-transform ${w.active ? 'left-[14px]' : 'left-0.5'}`}></span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => onDelete(w.id)}
                        className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-100 hover:text-red-600 transition-colors text-foreground-400"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add word modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setFormOpen(false)}>
          <div className="bg-background-50 rounded-2xl w-full max-w-md mx-4 p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold text-foreground-950">Agregar palabra prohibida</h3>
              <button onClick={() => setFormOpen(false)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
                <i className="ri-close-line text-foreground-500 text-sm"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 mb-1.5">Palabra o frase</label>
                <input
                  type="text"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder="Ej: palabra_ofensiva"
                  className="w-full px-3 py-2.5 rounded-xl bg-background-100 text-foreground-800 text-xs border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 mb-1.5">Nivel de gravedad</label>
                <div className="flex gap-1.5">
                  {(['leve', 'medio', 'grave'] as WordLevel[]).map((l) => (
                    <button
                      key={l}
                      onClick={() => setNewLevel(l)}
                      className={`flex-1 px-3 py-2 rounded-lg text-[10px] font-semibold cursor-pointer transition-all ${
                        newLevel === l
                          ? l === 'grave' ? 'bg-red-500 text-background-50' : l === 'medio' ? 'bg-orange-500 text-background-50' : 'bg-amber-500 text-background-50'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {wordLevelLabels[l]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 mb-1.5">Acción automática</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {(Object.keys(wordActionLabels) as WordAction[]).map((a) => (
                    <button
                      key={a}
                      onClick={() => setNewAction(a)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-medium cursor-pointer transition-all ${
                        newAction === a ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${wordActionIcons[a]} text-[11px]`}></i>
                      {wordActionLabels[a]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <button onClick={() => setFormOpen(false)} className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">
                Cancelar
              </button>
              <button onClick={handleAdd} disabled={!newWord.trim()} className="flex-1 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                Agregar palabra
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}